import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { objectToFormData } from 'utils/objectToFormData';
import { Endpoints } from 'common/api/endpoints'; // IMPORTED Endpoints object
import qs from 'qs';

// VARIABLES
const CIOSUSERS_API_REDUCER_KEY = 'ciosUsersApi';
const BASE_URL = `${
  process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_DEV_BASE_URL
}`;

// REMOVED prepareQuery function - logic now in Endpoints.js

// Base Query
const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  timeout: 1500 * 60,
  prepareHeaders: (headers, { getState, endpoint }) => {
    const token = getState().auth.token;
    if (token && endpoint !== 'refresh') {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

// API Slice
export const ciosUsersApi = createApi({
  reducerPath: CIOSUSERS_API_REDUCER_KEY,
  baseQuery: baseQuery,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: ['User Details', 'Claims', 'Plans', 'Stripe Payment', 'Properties'],
  endpoints: (builder) => ({
    // AUTHENTICATION
    login: builder.mutation({
      query: (body) => ({
        url: Endpoints.authLogin(), // CHANGED
        method: 'POST',
        body,
      }),
    }),
    register: builder.mutation({
      query: (body) => ({
        url: Endpoints.registerAsTenant(), // CHANGED
        method: 'POST',
        body,
      }),
    }),
    registerWithProperty: builder.mutation({
      query: (body) => ({
        url: Endpoints.registerTenantWithProperty(),
        method: 'POST',
        body,
      }),
    }),
    resendEmailToken: builder.mutation({
      query: () => ({
        url: Endpoints.resendTenantConfirmEmail(), // CHANGED
        method: 'POST',
      }),
    }),
    confirmEmail: builder.mutation({
      query: (emailToken) => ({
        url: Endpoints.confirmTenantEmail({ confirmEmailJWT: emailToken }), // CHANGED
        method: 'POST',
      }),
      invalidatesTags: ['User Details'],
    }),
    impersonationToken: builder.mutation({
      query: (body) => ({
        url: Endpoints.loginTokenAsTenant(), // CHANGED
        method: 'POST',
        body,
      }),
    }),
    retrieveUserDetails: builder.query({
      query: () => ({
        url: Endpoints.getMeTenant(), // CHANGED
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['User Details'],
    }),
    updatePassword: builder.mutation({
      query: (body) => ({
        url: Endpoints.updatePasswordAsTenant(), // CHANGED
        body,
        method: 'PATCH',
      }),
    }),
    resetPasswordRequest: builder.mutation({
      query: (body) => ({
        url: Endpoints.resetTenantPasswordRequest(), // CHANGED
        body,
        method: 'POST',
      }),
    }),
    resetPassword: builder.mutation({
      query: ({ password, token }) => ({
        url: Endpoints.resetTenantPassword({ resetPasswordJWT: token }), // CHANGED
        body: {
          password,
        },
        method: 'POST',
      }),
    }),
    // Kept as is - modifies headers, not just URL
    retrieveSpecialUserDetails: builder.query({
      query: (token) => ({
        url: Endpoints.getMeTenant(), // Use endpoint for URL consistency if needed
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
      transformResponse: (response) => response?.data,
      providesTags: ['User Details'],
    }),
    confirmAccount: builder.mutation({
      query: (body) => ({
        url: Endpoints.confirmTenantAccount(), // CHANGED
        body,
        method: 'POST',
      }),
    }),
    emailLoginRequest: builder.mutation({
      query: (body) => ({
        url: Endpoints.emailLoginRequest(), // CHANGED
        body,
        method: 'POST',
      }),
    }),
    emailLoginExchange: builder.mutation({
      query: (token) => ({
        url: Endpoints.emailLoginExchange(), // CHANGED
        body: { jwt: token },
        method: 'POST',
      }),
    }),

    // Owners (Tenant context)
    getSingleOwnerAsGuest: builder.query({
      query: (id) => ({
        url: Endpoints.getOwnerAsGuest({ ownerID: id }), // CHANGED
      }),
      providesTags: ['Owners'],
      transformResponse: (response) => response?.data,
    }),
    findOwnerPropertiesAsGuest: builder.query({
      query: (id) => ({
        url: Endpoints.findOwnerPropertiesAsGuest({ ownerID: id }), // CHANGED
      }),
      providesTags: ['Properties'],
      transformResponse: (response) => response?.data,
    }),
    getOwnerDistinctPropertyAddressAsGuest: builder.query({
      query: (id) => ({
        url: Endpoints.getOwnerDistinctPropertyAddressesAsGuest({ ownerID: id }), // CHANGED
      }),
      providesTags: ['Properties'],
      transformResponse: (response) => response?.data,
    }),

    // Property (Tenant context)
    getSinglePropertyAsGuest: builder.query({
      query: (id) => ({
        url: Endpoints.getPropertyAsGuest({ propertyID: id }),
      }), // CHANGED
      providesTags: ['Properties'],
      transformResponse: (response) => response?.data,
    }),
    getSingleProperty: builder.query({
      query: (id) => ({
        url: Endpoints.getPropertyAsTenant({ propertyID: id }),
      }), // CHANGED
      providesTags: ['Properties'],
      transformResponse: (response) => response?.data,
    }),
    findProperties: builder.query({
      query: (params) => ({ url: Endpoints.findAsTenantProperties(params) }), // CHANGED (removed prepareQuery)
      providesTags: ['Properties'],
      transformResponse: (response) => response?.data || response,
    }),
    findPropertyAddresses: builder.query({
      query: (params) => ({
        url: Endpoints.getDistinctPropertyAddresses(params),
      }), // CHANGED (removed prepareQuery)
      providesTags: ['Properties'],
      transformResponse: (response) => response?.data,
    }),

    // CLAIMS (Tenant context)
    getAllClaims: builder.query({
      query: (params) => ({ url: Endpoints.getClaimsAsTenant(params) }), // CHANGED (removed prepareQuery)
      providesTags: ['Claims'],
      transformResponse: (response) => response?.data || response,
    }),
    createClaim: builder.mutation({
      query: ({ value }) => ({
        url: Endpoints.createClaimAsTenant(), // CHANGED
        method: 'POST',
        body: value,
      }),
      invalidatesTags: ['Claims', 'Plans'],
    }),
    updateClaim: builder.mutation({
      query: ({ claimId, ...args }) => ({
        url: Endpoints.updateClaimAsTenant({ claimID: claimId }), // CHANGED
        method: 'PATCH',
        body: args,
      }),
      invalidatesTags: ['Claims', 'Plans'],
    }),
    submitClaim: builder.mutation({
      query: ({ claimId, ...args }) => ({
        url: Endpoints.submitClaimAsTenant({ claimID: claimId }), // CHANGED
        method: 'POST',
        body: args,
      }),
      invalidatesTags: ['Claims', 'Plans'],
    }),
    payClaim: builder.mutation({
      query: ({ claimId }) => ({
        url: Endpoints.paymentClaimAsTenant({ claimID: claimId }), // CHANGED
        method: 'POST',
        body: {}, // Assuming empty body based on old structure
      }),
      invalidatesTags: ['Claims', 'Plans'],
    }),
    getSingleClaim: builder.query({
      query: (id) => ({ url: Endpoints.getClaimAsTenant({ claimID: id }) }), // CHANGED
      providesTags: ['Claims'],
      keepUnusedDataFor: 0,
      transformResponse: (response) => response?.data,
    }),
    finalizeAClaim: builder.mutation({
      query: ({ claimId }) => ({
        url: Endpoints.finalizeClaimAsTenant({ claimID: claimId }), // CHANGED
        method: 'POST',
      }),
      invalidatesTags: ['Claims'],
    }),
    cancelClaim: builder.mutation({
      query: ({ claimId }) => ({
        url: Endpoints.deleteClaimAsTenant({ claimID: claimId }), // CHANGED (maps to delete)
        method: 'DELETE',
      }),
      invalidatesTags: ['Claims'],
    }),

    // PLANS (Tenant context)
    getAllPlans: builder.query({
      query: (args) => ({ url: Endpoints.getPlansAsTenant(args) }), // CHANGED (removed prepareQuery)
      providesTags: ['Plans'],
      transformResponse: (response) => response?.data,
    }),
    getSinglePlan: builder.query({
      query: (id) => ({ url: Endpoints.getPlanAsTenant({ planID: id }) }), // CHANGED
      providesTags: ['Plans'],
      transformResponse: (response) => response?.data,
    }),
    getDashboardMessages: builder.query({
      query: () => ({ url: Endpoints.getTenantMessages() }), // CHANGED
      providesTags: ['User Details'],
      transformResponse: (response) => response?.data,
    }),
    downloadPlanPdf: builder.mutation({
      query: (id) => ({
        url: Endpoints.getPlanPdfAsTenant({ planID: id }), // CHANGED
        method: 'GET',
        responseHandler: async (resp) => {
          // ... (response handling logic remains the same) ...
          const url = window.URL.createObjectURL(await resp.blob());
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `Plan-${id}_Details.pdf`);
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
          window.URL.revokeObjectURL(url);
          return 'Downloaded';
        },
        cache: 'no-cache',
      }),
    }),
    createPlan: builder.mutation({
      query: (value) => ({
        url: Endpoints.createPlanAsTenant(), // CHANGED
        method: 'POST',
        body: value,
      }),
      invalidatesTags: ['Plans', 'Claims', 'User Details'],
      transformResponse: (response) => response?.data,
    }),
    updateSinglePlan: builder.mutation({
      query: ({ planId, ...body }) => ({
        url: Endpoints.updatePlanAsTenant({ planID: planId }), // CHANGED
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Plans'],
      transformResponse: (response) => response?.data,
    }),
    getActivatePlan: builder.query({
      // Mapped to getSinglePlan as 'getActivatePlan' endpoint doesn't exist in endpoints.js
      query: (id) => ({ url: Endpoints.getPlanAsTenant({ planID: id }) }), // CHANGED
      invalidatesTags: ['Plans'],
      transformResponse: (response) => response?.data,
      transformErrorResponse: (response) => response?.data,
    }),
    activatePlan: builder.mutation({
      // Mapped to submitPlanAsTenant as 'activatePlan' endpoint doesn't exist
      query: ({ planId, ...body }) => ({
        url: Endpoints.submitPlanAsTenant({ planID: planId }), // CHANGED
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Plans'],
      transformResponse: (response) => response?.data,
      transformErrorResponse: (response) => response?.data,
    }),
    submitAPlan: builder.mutation({
      query: ({ planId, body }) => ({
        url: Endpoints.submitPlanAsTenant({ planID: planId }), // CHANGED
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Plans'],
      transformResponse: (response) => response?.data,
      transformErrorResponse: (response) => response?.data,
    }),
    deleteAPlan: builder.mutation({
      query: (planId) => ({
        url: Endpoints.deletePlanAsTenant({ planID: planId }), // CHANGED
        method: 'DELETE',
      }),
      invalidatesTags: ['Plans', 'Claims'],
    }),
    addDocToPlan: builder.mutation({
      query: ({ documentUrl, planID }) => ({
        url: Endpoints.createDocumentOnPlanAsTenant({ planID }), // CHANGED
        method: 'POST',
        body: {
          name: '', // Assuming default name if not provided
          url: documentUrl,
        },
      }),
      invalidatesTags: ['Plans'],
    }),
    getCoverageComparison: builder.query({
      query: (planId) => ({ 
        url: Endpoints.getCoverageComparisonAsTenant({ planID: planId }) 
      }),
      providesTags: ['Plans'],
      transformResponse: (response) => response?.data,
    }),
    deleteDocFromPlan: builder.mutation({
      query: ({ planID, documentID }) => ({
        url: Endpoints.deleteDocumentOnPlanAsTenant({ planID, documentID }), // CHANGED
        method: 'DELETE',
      }),
      invalidatesTags: ['Plans'],
    }),

    // USER DOCUMENT VERIFICATION (Tenant context)
    addDocToUser: builder.mutation({
      query: ({ name, url }) => ({
        url: Endpoints.createDocumentAsTenant(), // CHANGED
        method: 'POST',
        body: { name, url },
      }),
      invalidatesTags: ['User Details'],
    }),
    deleteDocFromUser: builder.mutation({
      query: (documentID) => ({
        url: Endpoints.deleteDocumentAsTenant({ documentID }), // CHANGED
        method: 'DELETE',
      }),
      invalidatesTags: ['User Details'],
    }),

    // UPLOAD DOCUMENT/SINGLE PDF - Kept as is, not in Endpoints.js
    uploadSinglePDF: builder.mutation({
      query: (value) => {
        const formDataValue = objectToFormData(value);
        return {
          url: `${Endpoints.UPLOADS}/pdf/single`, // Reconstruct manually if needed
          method: 'POST',
          body: formDataValue,
          formData: true,
        };
      },
    }),
    uploadDocument: builder.mutation({
      query: (document) => {
        const formDataValue = objectToFormData(document);
        return {
          url: `${Endpoints.UPLOADS}/doc/single`, // Reconstruct manually if needed
          method: 'POST',
          body: formDataValue,
          formData: true,
        };
      },
    }),
    uploadRequestUrl: builder.mutation({
      query: (body) => ({
        url: Endpoints.requestUploadUrlTenant(), // CHANGED
        method: 'POST',
        body,
      }),
    }),

    // STRIPE VERIFICATION SESSION (Tenant context)
    createVerificationSession: builder.mutation({
      query: () => ({
        url: Endpoints.createVerificationAsTenant(), // CHANGED
        method: 'POST',
      }),
      invalidatesTags: ['User Details'],
    }),

    // STRIPE PAYMENT (Tenant context)
    requestSetupIntent: builder.mutation({
      query: () => ({
        url: Endpoints.requestSetupIntentAsTenant(), // CHANGED
        method: 'POST',
      }),
      invalidatesTags: ['Stripe Payment'],
    }),
    addSetupIntent: builder.mutation({
      query: ({ setupIntentID, makeDefault }) => ({
        // Destructure args
        url: Endpoints.addSetupIntentAsTenant({ setupIntentID }), // CHANGED
        method: 'POST',
        body: { makeDefault: makeDefault ? true : false },
      }),
      invalidatesTags: ['Stripe Payment', 'User Details'],
    }),
    showAvailableMethods: builder.query({
      query: () => ({ url: Endpoints.getPaymentMethodsAsTenant() }), // CHANGED
      providesTags: ['Stripe Payment'],
    }),
    deletePaymentMethod: builder.mutation({
      query: (paymentMethodID) => ({
        url: Endpoints.deletePaymentMethodAsTenant({ paymentMethodID }), // CHANGED
        method: 'DELETE',
      }),
      invalidatesTags: ['Stripe Payment'],
    }),
    markPaymentMethodAsDefault: builder.mutation({
      query: (paymentMethodID) => ({
        url: Endpoints.setDefaultPaymentMethodAsTenant({ paymentMethodID }), // CHANGED
        method: 'PATCH', // Assuming PATCH based on endpoint name
      }),
      invalidatesTags: ['Stripe Payment'],
    }),
    makePayment: builder.mutation({
      query: ({ planID, choices }) => ({
        url: Endpoints.paymentAsTenant({ planID }), // CHANGED
        body: { choices },
        method: 'POST',
      }),
      invalidatesTags: ['Stripe Payment'],
    }),
    // Removed makeOneTimePayment and makeMonthlyPayment as they seem deprecated/replaced by makePayment
  }),
});

// exporting Query Hooks
export const {
  // AUTH
  useLoginMutation,
  useRegisterMutation,
  useRegisterWithPropertyMutation,
  useResendEmailTokenMutation,
  useConfirmEmailMutation,
  useImpersonationTokenMutation,
  useLazyRetrieveUserDetailsQuery,
  useRetrieveUserDetailsQuery,
  useUpdatePasswordMutation,
  useResetPasswordRequestMutation,
  useResetPasswordMutation,
  useRetrieveSpecialUserDetailsQuery,
  useConfirmAccountMutation,
  useEmailLoginRequestMutation,
  useEmailLoginExchangeMutation,
  // Owners (Tenant)
  useGetSingleOwnerAsGuestQuery,
  useFindOwnerPropertiesAsGuestQuery,
  useGetOwnerDistinctPropertyAddressAsGuestQuery,
  // Properties (Tenant)
  useGetSinglePropertyQuery,
  useGetSinglePropertyAsGuestQuery,
  useFindPropertiesQuery,
  useFindPropertyAddressesQuery,
  // CLAIMS (Tenant)
  useGetAllClaimsQuery,
  useCreateClaimMutation,
  useUpdateClaimMutation,
  useSubmitClaimMutation,
  usePayClaimMutation,
  useGetSingleClaimQuery,
  useFinalizeAClaimMutation,
  useCancelClaimMutation,
  // PLANS (Tenant)
  useGetAllPlansQuery,
  useGetSinglePlanQuery,
  useGetDashboardMessagesQuery,
  useDownloadPlanPdfMutation,
  useCreatePlanMutation,
  useUpdateSinglePlanMutation,
  useSubmitAPlanMutation,
  useActivatePlanMutation, // Note: Mapped to submitPlanAsTenant
  useGetActivatePlanQuery, // Note: Mapped to getPlanAsTenant
  useDeleteAPlanMutation,
  useAddDocToPlanMutation,
  useDeleteDocFromPlanMutation,
  useGetCoverageComparisonQuery,
  // USER DOCUMENT VERIFICATION (Tenant)
  useAddDocToUserMutation,
  useDeleteDocFromUserMutation,
  // UPLOAD DOCUMENTS
  useUploadSinglePDFMutation,
  useUploadDocumentMutation,
  useUploadRequestUrlMutation,
  // STRIPE VERIFICATION SESSION (Tenant)
  useCreateVerificationSessionMutation,
  // STRIPE PAYMENT (Tenant)
  useRequestSetupIntentMutation,
  useAddSetupIntentMutation,
  useShowAvailableMethodsQuery,
  useDeletePaymentMethodMutation,
  useMarkPaymentMethodAsDefaultMutation,
  useMakePaymentMutation,
  // Removed hooks for makeOneTimePayment and makeMonthlyPayment
} = ciosUsersApi;
